// autopilot-doctor-release-remote-check.ts
//
// Close-out A6 (Luke Q5, 2026-09-30): Autopilot v1 releases only through
// GitLab. On a GitHub `release.remote` the candidate push still happened
// (with GitLab-only `-o merge_request.*` options), then remote evidence had no
// `gh` runner wired (`review-remote-adapter.ts` → `resource_missing`) and the
// mission sat at `blocked:release_flow` on every `run`, unexplained. This
// check runs in the doctor ops checks — shown by `avatar autopilot doctor` and
// blocking `run` at supervisor start — so the answer arrives before any ticket
// is dispatched or anything is pushed. A remote that cannot be read (git exits
// non-zero, or cannot even start) is only a warning: the push leg reports that
// with its own, more precise error.
//
// Review P3: the remote URL is NEVER printed (it may embed a PAT — only the
// host is), and the host is extracted here rather than with the shared
// credential-oriented parser, which returns null for scp forms without `git@`
// and for `git+ssh://` — every one of those is GitHub too. GitHub Enterprise on
// a custom domain cannot be recognised from the URL; the docs say "GitLab only".
import type { AutopilotCheckResult } from "./autopilot-doctor-manifest-checks.js";
import type { GitRunner } from "./commit-owner-git-ops.js";
import type { ReleaseConfig } from "./release-manifest-hash.js";
import { realGitRunner } from "./supervisor-git-runner.js";

const NAME = "Release remote (v1: chỉ GitLab)";

/** Lowercase host of a git remote URL (`scheme://[user@]host[:port]/…` or scp `[user@]host:path`). */
function remoteHost(url: string): string | undefined {
  const scheme = /^[a-z][a-z0-9+.-]*:\/\/(?:[^/@]*@)?([^/:?#]+)/i.exec(url);
  if (scheme) return scheme[1]?.toLowerCase();
  const scp = /^(?:[^@/:]+@)?([^/:]+):/.exec(url);
  return scp?.[1]?.toLowerCase();
}

function isGithubHostname(host: string | undefined): boolean {
  return host === "github.com" || (host?.endsWith(".github.com") ?? false);
}

export async function checkReleaseRemoteSupportedInV1(input: {
  missionWorkspace: string;
  release: ReleaseConfig | undefined;
  gitRunner?: GitRunner;
}): Promise<AutopilotCheckResult> {
  if (input.release === undefined) {
    return { name: NAME, status: "ok", detail: "config không có `release` — không kiểm." };
  }
  const remoteName = input.release.remote;
  const unreadable = (why: string): AutopilotCheckResult => ({
    name: NAME,
    status: "warn",
    detail: `không đọc được remote "${remoteName}" trong worktree mission (${why}).`,
  });
  let res: Awaited<ReturnType<GitRunner>>;
  try {
    res = await (input.gitRunner ?? realGitRunner)(
      ["remote", "get-url", remoteName],
      input.missionWorkspace,
    );
  } catch (err) {
    return unreadable(err instanceof Error ? err.message : String(err));
  }
  const url = res.stdout.trim();
  if (res.status !== 0 || url === "") return unreadable(res.stderr.trim());
  const host = remoteHost(url);
  if (isGithubHostname(host)) {
    return {
      name: NAME,
      status: "fail",
      detail:
        `release.remote "${remoteName}" trỏ tới GitHub (host ${host}) — Autopilot v1 chỉ release qua GitLab ` +
        "(Draft MR + bằng chứng pipeline). Bỏ `release` khỏi config để chạy mission không release, hoặc dùng remote GitLab.",
    };
  }
  return {
    name: NAME,
    status: "ok",
    detail: `remote "${remoteName}" (host ${host ?? "?"}) không phải GitHub.`,
  };
}
